import React, { useCallback, useMemo, useState } from "react";
import { assignInlineVars } from "@vanilla-extract/dynamic";
import { type AgGrid, EmptyState, Flex, Grid, HelpIndicator, Text } from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { Colors, Sizes } from "@jsq/ds/styles";
import { useFayAuthGateArmed } from "#hooks/useFayAuthGate";
import { useFayPackageStaging } from "#hooks/useFayPackageStaging";
import {
  buildCoverageRows,
  computePeriodColumns,
  type CoverageQueryRow,
  type CoverageRowData,
  formatReportingDate,
  getCellPresentation,
  packageFieldFor,
  toRenderedCellState,
} from "#utils/coverageGrid.utils";
import * as styles from "./styles_vanilla.css";

// Matrix sizing. `domLayout="normal"` keeps the header and pinned Fund column
// fixed while the body scrolls, so the grid shrinks to its content but caps at
// MAX_GRID_HEIGHT — a hundreds-of-entities list scrolls inside the grid instead
// of stretching the page.
const HEADER_HEIGHT = 40;
const ROW_HEIGHT = 44;
const MAX_VISIBLE_ROWS = 12;
// Headroom for the horizontal scrollbar the period columns can introduce, so it
// never clips the final row.
const SCROLLBAR_ALLOWANCE = 16;
const MAX_GRID_HEIGHT = HEADER_HEIGHT + ROW_HEIGHT * MAX_VISIBLE_ROWS + SCROLLBAR_ALLOWANCE;

export function gridHeightForRows(visibleRows: number): number {
  const content = HEADER_HEIGHT + visibleRows * ROW_HEIGHT + SCROLLBAR_ALLOWANCE;
  return Math.min(content, MAX_GRID_HEIGHT);
}

export type CoverageGridProps = {
  periods: readonly string[];
  rows: readonly CoverageQueryRow[];
  /** Called with the backing package id when a leaf cell that has a package is
   *  clicked, opening the package-detail slideout. */
  onPackageSelected: (packageId: string) => void;
};

export const FA_MANAGED_LABEL = "FA managed";
export const FA_MANAGED_TOOLTIP =
  "The fund admin manages this fund. Its packages appear once published.";

export const CoverageGrid: React.FC<CoverageGridProps> = ({
  periods,
  rows,
  onPackageSelected,
}) => {
  // Fay MVP August (XFRG-941) release gate — see coverageGrid.utils'
  // getCellPresentation for the off-path (pre-XFRG-1190 behavior). Read once
  // here and threaded through as a closure rather than called from inside
  // `CoverageCellRenderer`/`cellStyle`: `cellStyle` is a plain AG Grid callback,
  // not a component, so it cannot call a hook itself — both cell affordances
  // capture the flag the same way for consistency. Read through the auth-gate
  // context (not a direct flag read) so rendering outside a provider doesn't
  // drag a FlagProvider requirement into a host app or a test.
  const fayMVPAugust = useFayAuthGateArmed();
  // Release gate for the FA-managed tag.
  const fayPackageStaging = useFayPackageStaging();

  const rowData = useMemo(() => buildCoverageRows(rows), [rows]);

  // Columns are the backend periods plus any reporting date present in the data,
  // so a package on a non-quarter-end date (e.g. Jun 11) surfaces its own column.
  const columns = useMemo(() => computePeriodColumns(periods, rows), [periods, rows]);

  /** Pinned "Fund" column renderer: the fund name, single-lined, plus the
   *  FA-managed marker. The marker explains why the row behaves differently; it
   *  is not a permission signal, so with the flag on every caller type sees it.
   *
   *  A glyph rather than a tag, and the tooltip only elaborates: `HelpIndicator`
   *  is hover/focus-only and invisible on touch, so the signal itself must stay
   *  visible. `aria-label` carries it independently of the tooltip, which hangs
   *  off a non-focusable span and so never reaches a keyboard. */
  const renderFundNameCell = useCallback(
    (params: AgGrid.ICellRendererParams<CoverageRowData>) => (
      <Flex alignItems="center" gap={Sizes.SM} className={styles.cellFill}>
        <Text asSpan variant="regular-sm" lines={1} className={styles.fundName}>
          {params.data?.fundName ?? params.value}
        </Text>
        {fayPackageStaging && params.data?.isFaManaged ? (
          <HelpIndicator
            // Required by the type despite the docs claiming it is generated.
            // Keyed by fund so a scrolled matrix cannot repeat an id.
            id={`fa-managed-${params.data.fundId}`}
            content={FA_MANAGED_TOOLTIP}
          >
            {/* A button, not a bare Icon: HelpIndicator opens on focus as well
                as hover and an SVG takes neither, so a bare Icon leaves the
                explanation mouse-only. Disclosing the tooltip is the button's
                whole job, so it has no onClick. */}
            <button type="button" aria-label={FA_MANAGED_LABEL} className={styles.fundMarker}>
              {/* Neutral700, not 600: the colour foundation only guarantees AA
                  for tokens *darker* than Neutral600. */}
              <Icon type="lock" size="small" color={Colors.Neutral700} aria-hidden="true" />
            </button>
          </HelpIndicator>
        ) : null}
      </Flex>
    ),
    [fayPackageStaging],
  );

  /** Inline, like the Fund renderer above, so it can close over `fayMVPAugust`.
   *  A native `title` is avoided: not keyboard-reachable, announced
   *  inconsistently. */
  const renderCoverageCell = useCallback(
    (params: AgGrid.ICellRendererParams<CoverageRowData>) => {
      const state = toRenderedCellState(params.value);
      if (state === undefined) {
        return null;
      }
      const { icon, iconColor, label } = getCellPresentation(state, fayMVPAugust);
      return (
        <Flex
          alignItems="center"
          justifyContent="center"
          className={styles.cellFill}
          aria-label={label}
        >
          {icon ? <Icon type={icon} size="small" color={iconColor} aria-hidden="true" /> : null}
        </Flex>
      );
    },
    [fayMVPAugust],
  );

  const columnDefs = useMemo<AgGrid.ColDef<CoverageRowData>[]>(
    () => [
      {
        field: "fundName",
        headerName: "Fund",
        pinned: "left",
        width: 280,
        suppressMovable: true,
        cellRenderer: renderFundNameCell,
      },
      ...columns.map((period) => ({
        field: period,
        headerName: formatReportingDate(period),
        width: 150,
        suppressMovable: true,
        cellRenderer: renderCoverageCell,
        cellStyle: (params: AgGrid.CellClassParams<CoverageRowData>) => {
          const state = toRenderedCellState(params.value);
          const background = state
            ? getCellPresentation(state, fayMVPAugust).background
            : undefined;
          const hasPackage = !!params.data?.[packageFieldFor(period)];
          return {
            backgroundColor: background ?? "transparent",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: hasPackage ? "pointer" : "default",
          };
        },
      })),
    ],
    [columns, renderCoverageCell, renderFundNameCell, fayMVPAugust],
  );

  const defaultColDef = useMemo<AgGrid.ColDef<CoverageRowData>>(
    () => ({ sortable: false, resizable: false, suppressHeaderMenuButton: true }),
    [],
  );

  const [gridHeight, setGridHeight] = useState(() => gridHeightForRows(rowData.length));

  // Resize to the actual displayed row count, capped at MAX_GRID_HEIGHT.
  const onModelUpdated = useCallback((event: AgGrid.ModelUpdatedEvent<CoverageRowData>) => {
    setGridHeight(gridHeightForRows(event.api.getDisplayedRowCount()));
  }, []);

  // A period cell backed by a package drills into the package-detail slideout.
  const onCellClicked = useCallback(
    (event: AgGrid.CellClickedEvent<CoverageRowData>) => {
      const { column, data } = event;
      const packageId = data?.[packageFieldFor(column.getColId())];
      if (typeof packageId === "string" && packageId) {
        onPackageSelected(packageId);
      }
    },
    [onPackageSelected],
  );

  if (!columns.length) {
    return (
      <EmptyState
        illustration="searching"
        title="No reporting dates in range"
        description="Adjust the date range to see deliverable packages."
      />
    );
  }

  if (!rowData.length) {
    return (
      <EmptyState
        illustration="searching"
        title="No deliverable packages match"
        description="Try adjusting the filters or date range."
      />
    );
  }

  return (
    <div
      className={styles.gridScrollContainer}
      style={assignInlineVars({ [styles.gridHeightVar]: `${gridHeight}px` })}
    >
      <Grid<CoverageRowData>
        rowData={rowData}
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        onCellClicked={onCellClicked}
        onModelUpdated={onModelUpdated}
        domLayout="normal"
        suppressRowHoverHighlight
        suppressBrowserResizeObserver
        headerHeight={HEADER_HEIGHT}
        rowHeight={ROW_HEIGHT}
        animateRows={false}
      />
    </div>
  );
};
